/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact/hooks';
import {
  LegendContextMenuContext,
  LegendContextMenuConfig,
  ItemInfo,
  LegendSectionData
} from '../utils/UNSAFE_visTypes/legend';
import { type gestureTypes, useContextMenu } from '../hooks/PRIVATE_useVisContextMenu';
import { getItemInfo } from '../utils/PRIVATE_legendUtils/legendUtils';

type Props<D, S> = {
  isReadOnly: boolean;
  rootRef: Ref<HTMLDivElement>;
  contextMenuConfig?: LegendContextMenuConfig<D>;
  focusedItemInfo: ItemInfo;
  items?: D[];
  sections?: S[];
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
};

export const useLegendContextMenu = <K, D, S extends LegendSectionData<K, D> | void = void>({
  rootRef,
  contextMenuConfig,
  focusedItemInfo,
  items,
  sections,
  isReadOnly,
  onContextMenuDismissed
}: Props<D, S>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<LegendContextMenuContext<D>>({
    onContextMenuHandler: ({ gesture, target }) => {
      let elem, itemIdx, sectionIdx;
      if (gesture === 'keyboard') {
        itemIdx = focusedItemInfo.itemIdx;
        sectionIdx = focusedItemInfo.sectionIdx;
        if (items) {
          elem = rootRef.current?.querySelector(`[data-oj-item="${itemIdx}"]`);
        } else if (sections) {
          elem = rootRef.current?.querySelector( `[data-oj-item="${itemIdx}"][data-oj-section="${sectionIdx}"]`);
        }
      } else {
        //We search for the key using the target of the event
        itemIdx = getItemInfo(target as HTMLElement)?.itemIdx;
        if (sections) {
          sectionIdx = getItemInfo(target as HTMLElement)?.sectionIdx;
        }
      }
      let data;
      if (items) {
        data = itemIdx !== undefined ? items[itemIdx] : null;
      } else if (sections) {
        data =
          sectionIdx !== undefined && itemIdx !== undefined
            ? sections[sectionIdx]?.items[itemIdx]
            : null;
      }

      let context: LegendContextMenuContext<D>;
      if (data) {
        context = {
          type: 'item',
          data: data
        };
      } else {
        context = { type: 'background' };
      }
      return {
        context,
        elem
      };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig || isReadOnly //If there is no context menu renderer or if isReadOnly is true, we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};
